import React, { useState, useEffect } from 'react';
import { getProductImage } from '../../utils/imageHelpers';
import { formatCouponDiscount } from '../../utils/couponHelpers';

const CheckoutModal = ({
  cart,
  getCartTotal,
  deliverySlots,
  selectedSlot,
  setSelectedSlot,
  paymentMethod,
  setPaymentMethod,
  onClose,
  onModifyCart,
  processPayment,
  deliveryAddresses,
  setShowAddAddress,
  handleSetDefaultAddress,
  selectedAddressId,
  setSelectedAddressId,
  appliedCoupon,
  couponDiscount,
  couponCode = '',
  setCouponCode,
  couponError = '',
  handleApplyCoupon,
  handleRemoveCoupon,
  getSuggestedCoupons,
  getFinalTotal,
  appSettings = { taxRate: 5, freeShippingThreshold: 500, standardShippingFee: 50, payuEnabled: false, codEnabled: false },
  walletBalance = 0,
  onWalletUsed,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
}) => {
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmountInput, setWalletAmountInput] = useState('');

  const orderTotal = getFinalTotal ? getFinalTotal() : getCartTotal();
  const maxWalletUse = Math.min(walletBalance, orderTotal);
  const walletAmountToUse = useWallet ? Math.min(parseFloat(walletAmountInput) || maxWalletUse, maxWalletUse) : 0;
  const amountDue = Math.max(0, orderTotal - walletAmountToUse);

  // Auto-enable wallet when it fully covers the order
  useEffect(() => {
    if (walletBalance >= orderTotal && orderTotal > 0) {
      setUseWallet(true);
      setWalletAmountInput(String(orderTotal));
    }
  }, [walletBalance, orderTotal]);

  // Auto-reset payment method when its gateway gets disabled
  useEffect(() => {
    const onlineMethod = ['upi','card','wallet','netbanking'].includes(paymentMethod);
    const codFamilyMethod = paymentMethod === 'cod' || paymentMethod === 'upi_cod';
    if (onlineMethod && !appSettings.payuEnabled) {
      // Online selected but PayU disabled — fall back to COD if available
      if (appSettings.codEnabled !== false) setPaymentMethod('cod');
    } else if (codFamilyMethod && appSettings.codEnabled === false) {
      // COD/UPI-on-delivery selected but disabled — fall back to first online method if PayU available
      if (appSettings.payuEnabled !== false) setPaymentMethod('upi');
    }
  }, [appSettings.payuEnabled, appSettings.codEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleWallet = (checked) => {
    setUseWallet(checked);
    if (checked) setWalletAmountInput(String(maxWalletUse));
    else setWalletAmountInput('');
  };

  const validAddresses = Array.isArray(deliveryAddresses)
    ? deliveryAddresses.filter(a => a.address && a.address.trim())
    : [];
  const hasAddress = !!selectedAddressId && validAddresses.some(a => a.id === selectedAddressId);

  const handleConfirmPay = () => {
    if (!hasAddress) {
      alert('Please add and select a delivery address before placing your order.');
      return;
    }
    processPayment({ walletAmountToUse });
    // Only optimistically deduct for partial wallet use; full-wallet orders are handled inside processPayment
    if (walletAmountToUse > 0 && walletAmountToUse < orderTotal && onWalletUsed) onWalletUsed(walletAmountToUse);
  };

    return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-[300]">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Checkout</h2>
          <button onClick={onClose} className="text-gray-500">
            ✕
          </button>
        </div>

        {/* Cart Total for Express Checkout */}
        <div className="flex justify-between items-center bg-blue-50 border-2 border-blue-200 rounded-xl p-3 mb-4">
          <span className="font-semibold text-gray-700 text-lg">Cart Total</span>
          <span className="font-bold text-blue-700 text-xl">₹{getCartTotal()}</span>
        </div>

        <div className="mb-6">
          <h3 className="font-semibold text-gray-800 mb-3">Delivery Address</h3>
          <div className="space-y-3 mb-4">
            {validAddresses.length > 0 ? (
              validAddresses.map(addr => (
                <div
                  key={addr.id}
                  onClick={() => setSelectedAddressId && setSelectedAddressId(addr.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedAddressId === addr.id ? 'border-blue-600 bg-blue-50' : (addr.isDefault ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300')}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{addr.label} {addr.isDefault && <span className="text-sm text-blue-600 font-medium">(Default)</span>}</p>
                      <p className="text-sm text-gray-600">{addr.recipient_name} • {addr.phone}</p>
                      <p className="text-sm text-gray-500 mt-1">{addr.address}, {addr.city} {addr.pincode}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {selectedAddressId === addr.id && (
                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs">✓</div>
                      )}
                      {!addr.isDefault && (
                        <button onClick={(e) => { e.stopPropagation(); handleSetDefaultAddress && handleSetDefaultAddress(addr.id); }} className="text-sm text-blue-600">Set Default</button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-3 rounded-lg border-2 border-orange-200 bg-orange-50 text-center">
                <p className="text-orange-700 font-medium text-sm">Please add a delivery address to continue.</p>
                <p className="text-orange-500 text-xs mt-1">Tap "+ Add New Address" below.</p>
              </div>
            )}
            <button onClick={() => setShowAddAddress && setShowAddAddress(true)} className="text-sm text-blue-600">+ Add New Address</button>
          </div>

          <h3 className="font-semibold text-gray-800 mb-3">Select Delivery Slot</h3>
          <div className="space-y-2">
            {deliverySlots.map(slot => {
              // Revert to previous icons
              let icon = slot.icon;
              if (slot.id === 'morning') icon = '🌅';
              if (slot.id === 'afternoon') icon = '☀️';
              if (slot.id === 'evening') icon = '🌆';
              return (
                <button
                  key={slot.id}
                  onClick={() => setSelectedSlot(slot.id)}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                    selectedSlot === slot.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{icon}</span>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">{slot.label}</p>
                      <p className="text-sm text-gray-500">{slot.time}</p>
                    </div>
                  </div>
                  {selectedSlot === slot.id && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Order Summary</h3>
            {onModifyCart && (
              <button onClick={onModifyCart} className="text-xs text-blue-600 font-medium underline">
                Modify Order
              </button>
            )}
          </div>
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
            {(cart || []).map((item, index) => {
              const unitLabel = item.unit ? ` ${item.unit}` : '';
              const weightLabel = item.weight ? `${item.weight}${unitLabel}` : '';
              const hasDiscount = item.sale_price && item.sale_price < item.price;
              const priceValue = hasDiscount ? item.sale_price : item.price;
              const originalTotal = Number(item.price || 0) * Number(item.quantity || 0);
              const saleTotal = Number(priceValue || 0) * Number(item.quantity || 0);
              const savings = hasDiscount ? (Number(item.price) - Number(item.sale_price)) * Number(item.quantity) : 0;
              return (
                <div key={`${item.id}-${index}`} className="flex flex-col gap-1 border-b last:border-b-0 pb-2 mb-2 last:pb-0 last:mb-0">
                  <div className="flex items-start justify-between text-sm gap-3">
                    <div className="flex items-center gap-3">
                      {getProductImage(item, 'small')}
                      <div>
                        <p className="font-medium text-gray-800">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {weightLabel ? `${weightLabel} • ` : ''}Qty: {item.quantity}
                        </p>
                      </div>
                    </div>
                    <div className="text-right min-w-[80px]">
                      {hasDiscount ? (
                        <>
                          <span className="text-xs text-gray-400 line-through block">₹{Number(item.price).toFixed(2)}</span>
                          <span className="text-base font-bold text-green-700 block">₹{Number(item.sale_price).toFixed(2)}</span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-gray-700 block">₹{Number(item.price).toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 mt-1">
                    <span>Total: <span className="font-semibold text-gray-800">₹{saleTotal.toFixed(2)}</span></span>
                    {hasDiscount && (
                      <span className="text-green-600 font-semibold">You save ₹{savings.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Wallet section — appears above payment methods */}
          {walletBalance > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">👛</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Use Wallet Balance</p>
                    <p className="text-xs text-gray-500">Available: ₹{walletBalance.toFixed(2)}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={useWallet}
                    onChange={(e) => handleToggleWallet(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              {useWallet && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">₹</span>
                    <input
                      type="number"
                      min="1"
                      max={maxWalletUse}
                      value={walletAmountInput}
                      onChange={(e) => setWalletAmountInput(e.target.value)}
                      className="flex-1 border border-blue-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                    />
                    <button
                      onClick={() => setWalletAmountInput(String(maxWalletUse))}
                      className="text-xs text-blue-600 font-medium hover:underline"
                    >
                      Max
                    </button>
                  </div>
                  {walletAmountToUse > 0 && (
                    <p className="text-xs text-green-600 mt-1.5 font-medium">
                      ✓ ₹{walletAmountToUse.toFixed(2)} will be deducted from wallet
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {amountDue <= 0 ? (
            /* Wallet covers the full order — no other payment needed */
            <div className="flex items-center gap-3 bg-green-50 border-2 border-green-400 rounded-xl p-4">
              <span className="text-2xl">👛</span>
              <div>
                <p className="font-semibold text-green-700">Paying fully with Wallet</p>
                <p className="text-sm text-green-600">No additional payment required</p>
              </div>
              <div className="ml-auto w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs">✓</span>
              </div>
            </div>
          ) : (
            <>

              <h3 className="font-semibold text-gray-800 mb-3">Payment Method</h3>
              <div className="space-y-2">
                {/* COD controlled by appSettings.codEnabled */}
                {appSettings.codEnabled !== false && (
                  <>
                    <button
                      onClick={() => setPaymentMethod('cod')}
                      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                        paymentMethod === 'cod'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">💵</span>
                        <div className="text-left">
                          <p className="font-semibold text-gray-800">Cash on Delivery</p>
                          <p className="text-sm text-gray-500">Pay when you receive</p>
                        </div>
                      </div>
                      {paymentMethod === 'cod' && (
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </button>

                    <button
                      onClick={() => setPaymentMethod('upi_cod')}
                      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                        paymentMethod === 'upi_cod'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">💵</span>
                        <div className="text-left">
                          <p className="font-semibold text-gray-800">Cash on Delivery (UPI)</p>
                          <p className="text-sm text-gray-500">Pay via UPI QR at delivery</p>
                        </div>
                      </div>
                      {paymentMethod === 'upi_cod' && (
                        <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </button>

                    {/* Inline QR preview when UPI Scan on Delivery is selected */}
                    {paymentMethod === 'upi_cod' && (
                      <div className="border-2 border-purple-200 bg-purple-50 rounded-xl p-4 flex flex-col items-center">
                        <p className="text-xs font-semibold text-purple-700 mb-2">UPI QR Code (shown at delivery)</p>
                        <img
                          src={upiQrCode}
                          alt="UPI QR Code"
                          style={{ width: 160, height: 160, borderRadius: 10, display: 'block' }}
                        />
                        {upiId && (
                          <p className="text-xs font-semibold text-purple-800 mt-2 text-center">{upiId}</p>
                        )}
                        <p className="text-xs text-purple-500 mt-1 text-center">The delivery agent will scan this QR</p>
                      </div>
                    )}
                  </>
                )}

                {/* Online payments (UPI / Card / Wallet) — all controlled by appSettings.payuEnabled */}
                {appSettings.payuEnabled !== false && (
                <>
                <button
                  onClick={() => setPaymentMethod('upi')}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                    paymentMethod === 'upi'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📱</span>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">UPI Payment</p>
                      <p className="text-sm text-gray-500">PhonePe, GPay, Paytm</p>
                    </div>
                  </div>
                  {paymentMethod === 'upi' && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                    paymentMethod === 'card'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💳</span>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">Credit/Debit Card</p>
                      <p className="text-sm text-gray-500">Visa, Mastercard, Rupay</p>
                    </div>
                  </div>
                  {paymentMethod === 'card' && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => setPaymentMethod('wallet')}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                    paymentMethod === 'wallet'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">👛</span>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">Wallet</p>
                      <p className="text-sm text-gray-500">Paytm, Amazon Pay</p>
                    </div>
                  </div>
                  {paymentMethod === 'wallet' && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>
                </>
                )}

                {/* No payment options available */}
                {appSettings.codEnabled === false && appSettings.payuEnabled === false && (
                  <div className="p-4 rounded-xl border-2 border-orange-200 bg-orange-50 text-center">
                    <p className="text-orange-700 font-medium text-sm">No payment methods are currently available.</p>
                    <p className="text-orange-500 text-xs mt-1">Please contact support or try again later.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Free Shipping Motivation Message */}
        {(() => {
          const cartTotal = getCartTotal() - (couponDiscount || 0);
          const remaining = appSettings.freeShippingThreshold - cartTotal;
          
          if (cartTotal >= appSettings.freeShippingThreshold) {
            return (
              <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-400 rounded-xl p-3 mb-4 flex items-center gap-2">
                <div className="text-2xl">🎉</div>
                <div className="flex-1">
                  <p className="font-bold text-green-700 text-sm">You qualify for FREE shipping!</p>
                  <p className="text-xs text-green-600">No delivery charges</p>
                </div>
              </div>
            );
          } else if (remaining > 0) {
            return (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-400 rounded-xl p-3 mb-4 flex items-center gap-2">
                <div className="text-2xl">🚚</div>
                <div className="flex-1">
                  <p className="font-bold text-blue-700 text-sm">Add ₹{remaining.toFixed(0)} more for FREE shipping!</p>
                  <p className="text-xs text-blue-600">Save ₹{appSettings.standardShippingFee} on delivery</p>
                </div>
              </div>
            );
          }
        })()}

        {/* Coupon Section */}
        <div className="mb-4">
          {appliedCoupon ? (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">🏷️</span>
                <div>
                  <p className="text-sm font-semibold text-green-700">{appliedCoupon.code}</p>
                  <p className="text-xs text-green-600">You save ₹{couponDiscount}</p>
                </div>
              </div>
              <button
                onClick={handleRemoveCoupon}
                className="text-xs text-red-500 font-medium hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition-all"
              >
                Remove
              </button>
            </div>
          ) : (
            <div>
              {/* Manual input */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={couponCode}
                  onChange={e => setCouponCode && setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleApplyCoupon && handleApplyCoupon()}
                  placeholder="Enter coupon code"
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400 uppercase placeholder:normal-case"
                />
                <button
                  onClick={handleApplyCoupon}
                  className="px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-all whitespace-nowrap"
                >
                  Apply
                </button>
              </div>
              {couponError && (
                <p className="text-xs text-red-500 mb-2 ml-1">{couponError}</p>
              )}

              {/* Eligible coupons list */}
              {getSuggestedCoupons && (() => {
                const suggested = getSuggestedCoupons();
                if (!suggested || suggested.length === 0) return null;
                return (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Available Coupons</p>
                    <div className="space-y-2">
                      {suggested.map(coupon => {
                        const eligible = getCartTotal() >= coupon.minOrder;
                        const shortfall = coupon.minOrder - getCartTotal();
                        return (
                          <div
                            key={coupon.code}
                            className={`border rounded-xl p-3 flex items-center justify-between gap-3 ${eligible ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50 opacity-70'}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-bold text-sm text-blue-700 tracking-wide">{coupon.code}</span>
                                <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                                  {formatCouponDiscount(coupon)}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 truncate">
                                {eligible
                                  ? coupon.description
                                  : `Add ₹${shortfall.toFixed(0)} more to unlock`}
                              </p>
                            </div>
                            <button
                              disabled={!eligible}
                              onClick={() => handleApplyCoupon && handleApplyCoupon(coupon.code)}
                              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${eligible ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                            >
                              Apply
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-semibold">₹{getCartTotal()}</span>
          </div>
          {appliedCoupon && couponDiscount > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-green-600">Coupon Discount ({appliedCoupon.code})</span>
              <span className="font-semibold text-green-600">-₹{couponDiscount}</span>
            </div>
          )}
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Shipping</span>
            <span className="font-semibold">
              {getCartTotal() >= appSettings.freeShippingThreshold ? (
                <span className="text-green-600">Free</span>
              ) : (
                `₹${appSettings.standardShippingFee}`
              )}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Tax (GST {appSettings.taxRate}%)</span>
            <span className="font-semibold">₹{(getCartTotal() * (appSettings.taxRate / 100)).toFixed(2)}</span>
          </div>
          {walletAmountToUse > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-blue-600">Wallet Deduction</span>
              <span className="font-semibold text-blue-600">-₹{walletAmountToUse.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between">
              <span className="text-lg font-bold">
                {walletAmountToUse > 0 ? 'Amount Due' : 'Total'}
              </span>
              <span className="text-lg font-bold text-blue-600">
                ₹{amountDue.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {!hasAddress && (
          <p className="text-orange-600 text-sm text-center mb-2 font-medium">
            Add a delivery address to place your order.
          </p>
        )}
        {validAddresses.length > 0 && !hasAddress && (
          <p className="text-red-500 text-sm text-center mb-2">Please select a delivery address.</p>
        )}
        <button
          onClick={handleConfirmPay}
          disabled={!hasAddress || (appSettings.codEnabled === false && appSettings.payuEnabled === false)}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
            !hasAddress || (appSettings.codEnabled === false && appSettings.payuEnabled === false)
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
          }`}>
          {amountDue <= 0
            ? 'Confirm & Pay with Wallet'
            : `Confirm & Pay ₹${amountDue.toFixed(2)}`}
        </button>

        {onModifyCart && (
          <button
            onClick={onModifyCart}
            className="w-full py-3 mt-3 rounded-xl font-semibold text-base border-2 border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-all">
            Modify Order
          </button>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;